import { type ButtonHTMLAttributes, type ReactNode, forwardRef } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * Button (DESIGN.md §6). One radius (`rounded-control`), three heights
 * (32/36/40), five intents. Never a pill: product-tokens.test.ts enforces it.
 */
export const buttonVariants = cva(
  [
    'inline-flex shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-control font-medium',
    'transition-[background-color,color,border-color,transform,opacity] duration-150 ease-out',
    'active:scale-[0.98] touch-manipulation select-none',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-canvas',
    'disabled:pointer-events-none disabled:opacity-50',
    '[&_svg]:size-4 [&_svg]:shrink-0',
  ],
  {
    variants: {
      variant: {
        primary: 'bg-accent text-accent-fg hover:bg-accent-hover',
        secondary:
          'border border-line bg-surface text-ink hover:border-line-strong hover:bg-surface-2',
        ghost: 'text-ink-2 hover:bg-surface-2 hover:text-ink',
        danger: 'bg-critical text-white hover:bg-critical/90',
        link: 'h-auto rounded-none px-0 text-accent-text underline-offset-4 hover:underline',
      },
      size: {
        sm: 'h-8 min-h-11 px-3 text-[13px] lg:min-h-0',
        md: 'h-9 min-h-11 px-3.5 text-sm lg:min-h-0',
        lg: 'h-10 min-h-11 px-4 text-sm lg:min-h-0',
      },
    },
    defaultVariants: { variant: 'primary', size: 'md' },
  }
);

export type ButtonVariant = NonNullable<VariantProps<typeof buttonVariants>['variant']>;

export type ButtonSize = NonNullable<VariantProps<typeof buttonVariants>['size']>;

/** Names still used by older call sites; mapped, not styled. */
export type LegacyButtonVariant = ButtonVariant | 'outline' | 'destructive' | 'default';

const LEGACY_ALIAS: Record<string, ButtonVariant> = {
  outline: 'secondary',
  destructive: 'danger',
  default: 'primary',
};

export function resolveButtonVariant(variant?: LegacyButtonVariant | null): ButtonVariant {
  if (!variant) return 'primary';

  // SAFETY: The surrounding typed contract establishes the asserted representation.
  return LEGACY_ALIAS[variant] ?? (variant as ButtonVariant);
}

export interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  variant?: LegacyButtonVariant;
  size?: ButtonSize;
  isLoading?: boolean;
  iconStart?: ReactNode;
  iconEnd?: ReactNode;
  children?: ReactNode;
}

/** Class string for elements that must be an `<a>` (downloads, links). */
export function buttonClasses(
  variant: LegacyButtonVariant = 'primary',
  size: ButtonSize = 'md',
  className?: string
): string {
  return cn(buttonVariants({ variant: resolveButtonVariant(variant), size }), className);
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant = 'primary',
      size = 'md',
      isLoading,
      disabled,
      iconStart,
      iconEnd,
      children,
      type = 'button',
      ...props
    },
    ref
  ) => (
    <button
      ref={ref}
      type={type}
      className={buttonClasses(variant, size, className)}
      disabled={disabled || isLoading}
      aria-busy={isLoading || undefined}
      {...props}
    >
      {isLoading ? <Loader2 className="animate-spin" aria-hidden="true" /> : iconStart}
      {children}
      {iconEnd}
    </button>
  )
);

Button.displayName = 'Button';
